import React from 'react'
import classnames from 'classnames/bind'
import { fromUnixTime } from 'date-fns'

import buildUrl from '@caudena/core/utils/buildUrl'
import useHttp from '@caudena/core/useHttp'
import { generatePath, useMatch, useNavigate } from '@caudena/core/Router/router'
import { Stub } from '@caudena/core/ui-kit'
import Portlet from '@caudena/core/Portlet'
import { CounterTabs, CounterTabPropsOption } from '@caudena/core/ui-kit'
import { HeaderSlot } from '@caudena/core/Layout/slots'
import { MagicGrid } from '@caudena/core/ui-kit'
import { Typography } from '@caudena/core/ui-kit'
import { Container } from '@caudena/core/ui-kit'
import { useFormatDate } from '../../hooks'
import { ActivityPeriod } from '@caudena/core/ui-kit'

import ActivitySummaryInfoCard from '../InfoCard/ActivitySummaryInfoCard'
import ClientScoreTimeline from '../ClientScoreTimeline'
import ClientHeader from '../ClientHeader'
import ExposureTab from './ExposureTab'
import DepositTab from './DepositTab'
import WithdrawalTab from './WithdrawalTab'
import TransfersTab from './TransfersTab'
import IncidentsTab from './IncidentsTab'
import CasesTab from './CasesTab'

import styles from './index.scss'
import { getConfig } from '@caudena/core/useConfig'
import { useSettings } from '../../hooks'
import { ClientDataResponse } from '../../apiServices/Client/ClientService.types'
import { normalizeSnakeToCamelCase } from '@caudena/core/utils/normalizeSnakeToCamelCase'
import { observer } from 'mobx-react-lite'
const cx = classnames.bind(styles)

interface ClientProps {
  id?: string
}

type TabType =
  | 'exposure'
  | 'transfers'
  | 'incidents'
  | 'cases'
  | 'deposit'
  | 'withdrawal'

const Client: React.FC<ClientProps> = ({ id }) => {
  const formatDate = useFormatDate()

  const { firstTransaction: firstTransactionSettings, settings } = useSettings()
  const navigate = useNavigate()

  const match = useMatch('/clients/:id/:tab')

  const tab = match ? (match.params.tab as TabType) : 'exposure'

  const handleUpdateTab = (tab: TabType) => {
    if (id) {
      navigate(generatePath('/clients/:id/:tab', { id, tab }))
    } else {
      throw new Error('Client ID is required')
    }
  }

  const [counters, setCounters] = React.useState({
    incidents: 0,
    cases: 0,
    transfers: 0,
    deposit: 0,
    withdrawals: 0,
  })

  const { data, isLoading } = useHttp<ClientDataResponse>(
    buildUrl`${getConfig()?.COMPLIANCE_API}/api/clients/${id}`
  )

  React.useEffect(() => {
    setCounters((prev) => ({
      ...prev,
      incidents: data?.client.total_incidents || 0,
      cases: data?.client.total_cases || 0,
      transfers: data?.client.transactions || 0,
      deposit: data?.client.total_deposits || 0,
      withdrawals: data?.client.total_withdrawals || 0,
    }))
  }, [data])

  HeaderSlot.useContent(
    () =>
      id ? (
        <ClientHeader withActions id={id} data={data?.client} />
      ) : (
        <Stub className={cx('PortletBodyStub')} isActive={true} />
      ),
    [data, id, data?.client]
  )

  const tabs: CounterTabPropsOption[] = [
    {
      value: 'exposure',
      children: 'exposure',
    },
    {
      value: 'transfers',
      children: 'transfers',
      counterValue: counters.transfers,
      isShowZeroValue: true,
    },
    {
      value: 'incidents',
      children: 'incidents',
      counterValue: counters.incidents,
      isShowZeroValue: true,
    },
    {
      value: 'cases',
      children: 'cases',
      counterValue: counters.cases,
      isShowZeroValue: true,
    },
    {
      value: 'deposit',
      children: 'deposit addr.',
      counterValue: counters.deposit,
      isShowZeroValue: true,
    },
    {
      value: 'withdrawal',
      children: 'withdrawal addr.',
      counterValue: counters.withdrawals,
      isShowZeroValue: true,
    },
  ]

  const firstTransaction = firstTransactionSettings
    ? fromUnixTime(firstTransactionSettings)
    : null

  const tabToView = {
    exposure: (
      <ExposureTab
        exposure={
          data?.exposure ? normalizeSnakeToCamelCase(data?.exposure) : undefined
        }
      />
    ),
    transfers: id ? <TransfersTab clientId={id} flow={data?.flow} /> : null,
    incidents: id ? <IncidentsTab clientId={id} /> : null,
    cases: id ? <CasesTab clientId={id} /> : null,
    deposit: id ? <DepositTab clientId={id} /> : null,
    withdrawal: id ? <WithdrawalTab clientId={id} /> : null,
  }

  const firstSeen = data?.client?.first_seen
    ? new Date(data?.client.first_seen * 1000)
    : null

  const lastSeen = data?.client?.last_seen
    ? new Date(data?.client.last_seen * 1000)
    : null

  const activityPeriod = firstSeen
    ? ([firstSeen, lastSeen] as [Date, Date])
    : null

  return (
    <div className={cx('ClientGrid')}>
      <Portlet variant="card">
        {{
          body: (
            <div className={cx('ClientMainInfo')}>
              <Container gap={1.5} className={cx('ActivitySummaryInfoCard')}>
                <MagicGrid gap={2}>
                  <Typography variant="heading3" color="grey1">
                    Activity summary
                  </Typography>
                  <ActivityPeriod
                    timeZone={settings?.timeZone}
                    activityPeriodStart={firstTransaction ?? undefined}
                    today={formatDate(new Date(), 'date')}
                    activityPeriod={activityPeriod ?? undefined}
                  />
                  <ActivitySummaryInfoCard
                    loading={isLoading}
                    client={data?.client}
                  />
                </MagicGrid>
              </Container>
              <ClientScoreTimeline
                data={data?.score_history}
                className={cx('ClientScoreTimeline')}
              />
            </div>
          ),
        }}
      </Portlet>
      <Portlet variant="card" className={cx('TabsPortlet')}>
        {{
          head: (
            <CounterTabs
              className={cx('ClientTabs', { disabled: isLoading })}
              options={tabs}
              value={!isLoading ? tab : ''}
              disabled={isLoading}
              onChange={handleUpdateTab}
            />
          ),
          body: !isLoading ? (
            tabToView[tab]
          ) : (
            <Stub className={cx('PortletBodyStub')} isActive={true} />
          ),
        }}
      </Portlet>
    </div>
  )
}

export default observer(Client)
